import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@store/hooks';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeftLong } from 'react-icons/fa6';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import {
    getDataUuid,
    getDataUsingDeviceId,
    getDataUsingUserId,
    formatDateDMY,
} from '@utils/sharedUtils';
import { DEVICE_CHANGE_LOG_TABLE } from '@constants/dynamoDBTableName';
import { ACTIVITY_LOG_PATH } from '@constants/routes';
import DeviceNurseList from './DeviceNurseList';
import Swal from 'sweetalert2';

const MySwal = Swal;

type NurseDetails = {
    name: string;
    oldValue: string;
    newValue: string;
};

interface DeviceDetailsModalProps {
    show: boolean;
    onHide: () => void;
}

const DeviceDetailsModal: React.FC<DeviceDetailsModalProps> = ({
    show,
    onHide,
}) => {
    const deviceDetails = useAppSelector((state) => state.user.deviceLog);
    const { t } = useTranslation();
    const [nurseDetails, setNurseDetails] = useState<NurseDetails[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        if (!deviceDetails) {
            navigate(ACTIVITY_LOG_PATH);
        }
        fetchDeviceDetails();
    }, [deviceDetails, navigate]);

    const fetchDeviceDetails = async () => {
        try {
            if (deviceDetails) {
                const device = await getDataUuid(
                    DEVICE_CHANGE_LOG_TABLE,
                    deviceDetails?.id
                );
                if (device) {
                    const nameMapping: Record<string, string> = {
                        RPM: 'Flow Rate',
                        T_L_Time: 'Infusion Time',
                        T_Qua: 'Drip Quantity',
                    };

                    const nonZeroValues = Object.entries(device)
                        .filter(
                            ([key, value]) =>
                                ['RPM', 'T_L_Time', 'T_Qua'].includes(key) &&
                                (key === 'RPM' || value.N !== '0')
                        )
                        .map(([key, value]) => ({
                            name: nameMapping[key as keyof typeof nameMapping],
                            oldValue:
                                device.meta_data?.M?.[
                                    `old_${key.toLowerCase()}`
                                ]?.N ?? '0',
                            newValue: value.N || '',
                        }));
                    setNurseDetails(nonZeroValues);
                }
            }
        } catch (error: any) {
            let errorMessage;
            switch (error.name) {
                case 'ResourceNotFoundException':
                    errorMessage = 'Error: The specified table does not exist.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage =
                        'Error: The request rate is too high. Please try again later.';
                    break;
                default:
                    errorMessage = 'An unknown error occurred.';
                    break;
            }
            MySwal.fire({
                icon: 'error',
                title: errorMessage,
                showConfirmButton: false,
                timer: 3000,
            });
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t('device_details.device_log')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row">
                    <div className="col">
                        <div className="py-3">
                            <label className="font-bold text-black">
                                {t('device_details.device_id')}:
                            </label>
                            <span className="ml-3">
                                {deviceDetails?.device_id || '--'}
                            </span>
                        </div>
                        <div className="py-3">
                            <label className="font-bold text-black">
                                {t('device_details.time')}:
                            </label>
                            <span className="ml-3">
                                {deviceDetails?.time || '--'}
                            </span>
                        </div>
                    </div>
                    <div className="col">
                        <div className="py-3">
                            <label className="font-bold text-black">
                                {t('device_details.device_name')}:
                            </label>
                            <span className="ml-3">
                                {deviceDetails?.device_name || '--'}
                            </span>
                        </div>
                        <div className="py-3">
                            <label className="font-bold text-black">
                                {t('device_details.modified_by')}:
                            </label>
                            <span className="ml-3">
                                {deviceDetails?.created_by || '--'}
                            </span>
                        </div>
                    </div>
                    <div className="col">
                        <div className="py-3">
                            <label className="font-bold text-black">
                                {t('device_details.added_date')}:
                            </label>
                            <span className="ml-3">
                                {deviceDetails?.created_at || '--'}
                            </span>
                        </div>
                        <div className="py-3">
                            <label className="font-bold text-black">
                                {t('device_details.role')}:
                            </label>
                            <span className="ml-3">
                                {deviceDetails?.role || '--'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Nurse Details Table */}
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>{t('device_details.name')}</th>
                            <th>{t('device_details.old_value')}</th>
                            <th>{t('device_details.new_value')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {nurseDetails.map((item) => (
                            <tr key={item.name}>
                                <td>{item.name}</td>
                                <td>{item.oldValue}</td>
                                <td>{item.newValue}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {t('device_details.close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DeviceDetailsModal;
